import React, { Component } from "react";
import { withRouter, History } from "react-router-dom";
import { compose } from "redux";

import posed from "react-pose";

import Camera from "./Camera";
import { StorableSearch } from "../classes/searchResponse";
import { SearchRequestService } from "../services/SearchRequestService";
import * as constants from "../constants";
import { ARTWORK, EXHIBITION, HOME } from "../constants/routes";
import { getVisitSessionId, getHeldEmail } from "../helpers/sessionId";
import withTranslation, { WithTranslationState } from "./withTranslation";
import { DebugOverlay } from "./DebugOverlay";
import { MatchConfirmation } from "./MatchConfirmation";
import { DEBUG_ENABLED } from "../helpers/debugEnabled";
import { track } from "../helpers/analytics";

const Container = posed.div({
  enter: { opacity: 1 },
  exit: { opacity: 0 },
});

type CameraContainerProps = {
  history: History;
} & WithTranslationState;

type DebugEntry = {
  attempt: number;
  rtt_ms: number;
  match_count: number;
  top_match: string | null;
  top_score: number | null;
  top_crop: string | null;
  top_inliers: number | null;
  image_size_kb: number | null;
  capture_res: string | null;
  thumbUrl: string | null;
  timestamp: number;
  submitted_at_ms: number | null;
  received_at_ms: number | null;
  // v2 engine additions (2026-06-10):
  //  - engine: which path actually served the query ("global" | "orb" | "sp_fallback")
  //  - top_corners: 4-point quad in original query pixel space (TL, TR, BR, BL)
  //    for the top match. Powers the corner-bracket confirmation UX and
  //    Discovery Mode in subsequent PRs.
  engine: string | null;
  top_corners: number[][] | null;
  // Damping streak at the time this entry was logged: how many consecutive
  // captures (including this one) returned the same top_match. The UI
  // surfaces a match only when this hits CONSECUTIVE_MATCH_THRESHOLD.
  consecutive_count: number;
  consecutive_threshold: number;
  // True when the engine flagged this frame's top match as strong enough to
  // open on its own (bypasses the consecutive-frame rule).
  strong_single?: boolean;
};

type MatchToConfirm = {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  corners: number[][];
};

type CameraContainerState = {
  scanSeqId: any;
  shouldBeScanning: boolean;
  sessionYieldedMatch: boolean;
  debugLog: DebugEntry[];
  tsttf: number | null;
  // Set the instant a match is identified, cleared on unmount. Triggers
  // the MatchConfirmation overlay which shows the captured frame with
  // SVG corner brackets at the engine-returned homography points.
  matchToConfirm: MatchToConfirm | null;
};

// Neighbor-flip damping threshold. Engine team's 2026-06-10 wide-scan-misID
// memo reported 42% of co-matched scans disagreed between wall neighbors.
// Their recommendation: require 2-3 consecutive frames returning the SAME
// target_id before surfacing the match to the visitor. Starting at 2 (lighter
// TSTTM cost, still cuts the bulk of flip cases). Bump to 3 if flips persist.
const CONSECUTIVE_MATCH_THRESHOLD = 2;

export class CameraContainerComponent extends Component<
  CameraContainerProps,
  CameraContainerState
> {
  sr: SearchRequestService;
  snapAttempts: number;
  responseCounter: number;
  scanStartTime: number;
  batchId: string;
  sessionId: string;
  lastScanAt: number;
  // Damping state: tracks consecutive same-id matches across the scan
  // session. Compares on `name` (artwork id) not target_id, so different
  // reference variants of the same artwork count as agreement.
  lastMatchedName: string | null;
  consecutiveMatchCount: number;
  // Phase timestamps for TSTTM breakdown — all Date.now()
  phaseFirstSubmit: number; // first frame POST kicked off
  phaseFirstMatch: number; // searchWasSuccessful=true
  phaseNavigation: number; // history.push fired
  // Wall-clock safety net for the no-match state. The responseCounter === 9
  // trigger misses if ImageCapture.grabFrame() throws on the device mid-scan
  // (5 responses arrive instead of 9, counter never hits 9). This timer fires
  // failure regardless of how many responses came back.
  failureTimeoutId: ReturnType<typeof setTimeout> | null;

  constructor(props) {
    super(props);

    this.sr = new SearchRequestService();
    this.snapAttempts =
      parseInt(localStorage.getItem(constants.SNAP_ATTEMPTS)) || 0;

    this.state = {
      scanSeqId: null,
      shouldBeScanning: null,
      sessionYieldedMatch: null,
      debugLog: [],
      tsttf: null,
      matchToConfirm: null,
    };
  }

  /**
   * Push a debug entry when a frame capture failed (ImageCapture.grabFrame
   * threw). These appear as "[CAPTURE FAILED]" rows in the debug overlay,
   * making it obvious why responseCounter is stuck below 9.
   */
  recordCaptureFailure = (reason: string) => {
    if (this.state.sessionYieldedMatch !== null) return;
    const entry: DebugEntry = {
      attempt: this.responseCounter,
      rtt_ms: 0,
      match_count: 0,
      top_match: `[CAPTURE FAILED — ${reason}]`,
      top_score: null,
      top_crop: null,
      top_inliers: null,
      image_size_kb: null,
      capture_res: null,
      thumbUrl: null,
      timestamp: Date.now(),
      submitted_at_ms: null,
      received_at_ms: null,
      engine: null,
      top_corners: null,
      consecutive_count: this.consecutiveMatchCount,
      consecutive_threshold: CONSECUTIVE_MATCH_THRESHOLD,
    };
    this.setState((prev) => ({ debugLog: [...prev.debugLog, entry] }));
  };

  /** Revokes any blob URLs from previous debug log entries to prevent memory leaks */
  revokeDebugThumbnails = () => {
    this.state.debugLog.forEach((entry) => {
      if (entry.thumbUrl) URL.revokeObjectURL(entry.thumbUrl);
    });
  };

  /** Starts a scanning session over a 3-second duration */
  beginScanning = () => {
    // FOC-136: resolve the visit session_id from the PREVIOUS last-scan (it
    // resets after >2h idle) BEFORE we stamp the new timestamp below.
    this.sessionId = getVisitSessionId();

    // FOC-143 2/3: if the visitor gave an email in a prior within-24h session,
    // re-submit it ONCE for THIS session, so a return-within-24h visit's scans
    // still get a recap without re-entry. Fire-and-forget; only fires on a
    // session the held email hasn't been ingested for yet.
    try {
      const heldEmail = getHeldEmail();
      if (
        heldEmail &&
        localStorage.getItem(constants.SNAP_EMAIL_INGESTED_SID) !==
          this.sessionId
      ) {
        this.sr.submitEmailIngest(this.sessionId, heldEmail);
        localStorage.setItem(constants.SNAP_EMAIL_INGESTED_SID, this.sessionId);
      }
    } catch {
      // best-effort — never block a scan
    }

    // Update the snap attempts with this scan as a single attempt
    localStorage.setItem(
      constants.SNAP_ATTEMPTS,
      (this.snapAttempts + 1).toString()
    );
    this.lastScanAt = Date.now();
    localStorage.setItem(
      constants.SNAP_LAST_TIMESTAMP,
      this.lastScanAt.toString()
    );

    // Reset scans taken and stop any existing scanning
    this.responseCounter = 0;
    this.scanStartTime = Date.now();
    this.phaseFirstSubmit = 0;
    this.phaseFirstMatch = 0;
    this.phaseNavigation = 0;
    this.batchId = crypto.randomUUID();
    this.snapAttempts = this.snapAttempts + 1;
    track("scan_attempt", { attempt_number: this.snapAttempts });
    // Reset damping streak — each new scan session starts fresh.
    this.lastMatchedName = null;
    this.consecutiveMatchCount = 0;

    // Revoke previous scan's blob URLs before clearing debugLog
    this.revokeDebugThumbnails();

    // Arm the wall-clock safety net. 6 seconds is generous — 9 frames at 3 fps
    // take ~3s of capture wall time plus ~250-400ms per response, so all 9
    // should be back well under 5s on a healthy run.
    if (this.failureTimeoutId) clearTimeout(this.failureTimeoutId);
    this.failureTimeoutId = setTimeout(() => this.forceFailureIfStillScanning(), 6000);

    this.setState({
      shouldBeScanning: true,
      scanSeqId: Date.now(),
      sessionYieldedMatch: null,
      debugLog: [],
      tsttf: null,
    });
  };

  /**
   * Trigger the no-match state regardless of whether all 9 frames returned.
   * Called both by the wall-clock timer and by the per-response handler when
   * responseCounter === 9. Idempotent — checks sessionYieldedMatch first.
   */
  forceFailureIfStillScanning = (reason: string = "wall-clock timeout") => {
    if (
      this.state.sessionYieldedMatch !== null ||
      !this.state.shouldBeScanning
    ) {
      return;
    }
    const timeoutMarker: DebugEntry = {
      attempt: this.responseCounter,
      rtt_ms: 0,
      match_count: 0,
      top_match: `[TIMEOUT — ${reason}; NoMatchOverlay should render]`,
      top_score: null,
      top_crop: null,
      top_inliers: null,
      image_size_kb: null,
      capture_res: null,
      thumbUrl: null,
      timestamp: Date.now(),
      submitted_at_ms: null,
      received_at_ms: null,
      engine: null,
      top_corners: null,
      consecutive_count: this.consecutiveMatchCount,
      consecutive_threshold: CONSECUTIVE_MATCH_THRESHOLD,
    };
    track("scan_no_match", { attempt_number: this.snapAttempts });
    this.setState((prev) => ({
      shouldBeScanning: false,
      sessionYieldedMatch: false,
      tsttf: Date.now() - this.scanStartTime,
      debugLog: [...prev.debugLog, timeoutMarker],
    }));
  };

  componentWillUnmount() {
    // If the user is navigating to /artwork the URLs were passed via router state
    // and the ArtworkWrapper will revoke them. If they navigated elsewhere, revoke here.
    if (!this.state.sessionYieldedMatch) {
      this.revokeDebugThumbnails();
    }
    // Revoke the match-confirmation blob URL — it's a separate object
    // URL from the debug thumbnails and isn't referenced anywhere after
    // navigation (the artwork detail page uses the CDN image, not the
    // captured frame).
    if (this.state.matchToConfirm?.imageUrl) {
      URL.revokeObjectURL(this.state.matchToConfirm.imageUrl);
    }
  }

  /** Executes steps for completing a scan from an image capture to determining a match */
  processImageCapture = async (imageBlob, captureRes?: string) => {
    // Only process image capture when we haven't determined if session yielded a match
    if (this.state.sessionYieldedMatch === null && this.responseCounter <= 9) {
      // TEST: thumbnail blob URLs disabled to verify whether the freeze
      // is GPU-process contention from img-element JPEG decoding/texture
      // upload. If freeze disappears with this change → confirmed cause;
      // we re-enable later with downsized thumbnails (encode small JPEG
      // before createObjectURL) to keep the diagnostic without the GPU
      // pressure. Production was already unaffected because this branch
      // is DEBUG_ENABLED-gated.
      const thumbUrl: string | null = null;

      // Timestamp: when we kicked off the POST. Used to diagnose whether
      // freezes correlate with submit-time, receive-time, or both.
      const submittedAtMs = Date.now();

      // Stamp the first-submit phase boundary on the very first frame POST
      if (!this.phaseFirstSubmit) this.phaseFirstSubmit = submittedAtMs;

      const imageSearchResponse = await this.sr.submitImageSearchRequest(
        imageBlob,
        this.batchId,
        this.sessionId,
        this.lastScanAt
      );

      // Timestamp: when the POST resolved (response arrived).
      const receivedAtMs = Date.now();

      // const { data } = imageSearchRequestConfig;
      const { searchWasSuccessful, searchTime } = imageSearchResponse;
      let searchResultToStore,
        elasticSearchResponse = null;

      // Increment the response counter
      this.responseCounter++;

      // Track debug info from the response (always create entry for thumbnail visibility).
      // Defensive: if server didn't return a debug field, fall back to searchWasSuccessful
      // so a real match doesn't display as "no match" gray in the overlay.
      const debugInfo = imageSearchResponse.responsePayload?.data?.debug;
      const fallbackMatchCount = searchWasSuccessful ? 1 : 0;

      // Neighbor-flip damping. Track consecutive same-id matches across
      // captures. Engine team's 2026-06-10 wide-scan-misID memo: 42% of
      // co-matched scans disagreed between wall neighbors. Requiring
      // CONSECUTIVE_MATCH_THRESHOLD same-id matches before surfacing the
      // result cuts the bulk of flips. A no-match (or a different id)
      // resets the streak. Computed BEFORE the debug entry so the entry
      // can record the current streak state for visibility.
      const matchedName = (debugInfo?.top_match as string | undefined) || null;
      if (searchWasSuccessful && matchedName) {
        if (matchedName === this.lastMatchedName) {
          this.consecutiveMatchCount += 1;
        } else {
          this.lastMatchedName = matchedName;
          this.consecutiveMatchCount = 1;
        }
      } else {
        this.lastMatchedName = null;
        this.consecutiveMatchCount = 0;
      }
      // Strong single frame: the engine flags a top match with enough
      // geometric agreement to trust on one frame (cut-off lives in engine
      // config, set from Steve's adjudication of one-frame scans). A missing
      // flag (older engine) counts as false, so the 2-in-a-row rule holds.
      const strongSingle =
        searchWasSuccessful && !!matchedName && debugInfo?.strong_single === true;
      const dampingMet =
        strongSingle ||
        this.consecutiveMatchCount >= CONSECUTIVE_MATCH_THRESHOLD;

      const entry: DebugEntry = {
        attempt: this.responseCounter,
        rtt_ms: debugInfo?.rtt_ms ?? 0,
        match_count: debugInfo?.match_count ?? fallbackMatchCount,
        top_match: debugInfo?.top_match || null,
        top_score: debugInfo?.top_score ?? null,
        top_crop: debugInfo?.top_crop || null,
        top_inliers: debugInfo?.top_inliers ?? null,
        image_size_kb: debugInfo?.image_size_kb ?? null,
        capture_res: captureRes || debugInfo?.capture_res || null,
        thumbUrl,
        timestamp: Date.now(),
        submitted_at_ms: submittedAtMs,
        received_at_ms: receivedAtMs,
        engine: debugInfo?.engine || null,
        top_corners: debugInfo?.top_corners || null,
        consecutive_count: this.consecutiveMatchCount,
        consecutive_threshold: CONSECUTIVE_MATCH_THRESHOLD,
        strong_single: strongSingle,
      };
      this.setState((prev) => ({
        debugLog: [...prev.debugLog, entry],
      }));

      // If search was successful AND damping threshold met
      if (searchWasSuccessful && dampingMet && this.state.shouldBeScanning) {
        this.phaseFirstMatch = Date.now();
        this.setState({ shouldBeScanning: false, sessionYieldedMatch: true });

        // Get the identified image
        const identifiedItem =
          imageSearchResponse.responsePayload.data.results[0];

        // Engine /v2 returns a 4-point quad in original query pixel space.
        // If present, show the corner-bracket confirmation overlay while
        // the artwork data is fetched in parallel. The overlay enforces a
        // minimum visible duration so the visitor sees "we identified it
        // *here*" before the result page mounts.
        const corners = identifiedItem?.corners as number[][] | undefined;
        const confirmStartedAt = Date.now();
        if (corners && corners.length === 4 && captureRes) {
          const [w, h] = captureRes.split("x").map(Number);
          if (Number.isFinite(w) && Number.isFinite(h) && imageBlob) {
            const imageUrl = URL.createObjectURL(imageBlob);
            this.setState({
              matchToConfirm: {
                imageUrl,
                imageWidth: w,
                imageHeight: h,
                corners,
              },
            });
          }
        }

        // Get language code
        const langPref = (await this.props.getSelectedLanguage())[0].code;
        const identifiedImageInformation = await this.sr.processIdentifiedItem(
          identifiedItem,
          langPref
        );
        const { esResponse, referenceImageUrl } = identifiedImageInformation;
        searchResultToStore = new StorableSearch(
          {},
          searchWasSuccessful,
          referenceImageUrl,
          esResponse,
          searchTime
        );
        elasticSearchResponse = esResponse;

        // Enforce minimum confirmation overlay visible duration. The
        // artwork fetch above is usually instant (in-memory cache), so
        // without this gate the visitor would see the brackets for
        // ~10ms and the navigation would feel like a hiccup. 400ms is
        // enough to register the visual + complete the corner-fade
        // animation; the artwork data is almost always ready first, so
        // this is the only thing the visitor actually waits for.
        if (this.state.matchToConfirm) {
          const MIN_CONFIRM_MS = 400;
          const elapsed = Date.now() - confirmStartedAt;
          if (elapsed < MIN_CONFIRM_MS) {
            await new Promise((r) => setTimeout(r, MIN_CONFIRM_MS - elapsed));
          }
        }

        if (elasticSearchResponse) {
          this.completeImageSearchRequest(elasticSearchResponse);
        } else {
          // The ES response for special exhibition objects can be undefined if no content has bee added to Hygraph for the object,
          //  In that case we will want to end scanning by updating the state to show that the search did not yield a match
          this.setState({
            sessionYieldedMatch: false,
          });
        }
      }

      // Otherwise, when it's not successful
      else {
        searchResultToStore = new StorableSearch(
          // data,
          {},
          searchWasSuccessful,
          null,
          elasticSearchResponse,
          searchTime
        );
      }

      // Store the result, regardless of success or not
      this.sr.storeSearchedResult(searchResultToStore);

      // Fast path: all 9 responses came back with no match. Clear the
      // wall-clock safety timer (since we're handling failure here) and
      // trigger the failure state via the shared helper.
      if (this.responseCounter === 9) {
        if (this.failureTimeoutId) {
          clearTimeout(this.failureTimeoutId);
          this.failureTimeoutId = null;
        }
        this.forceFailureIfStillScanning("9 of 9 responses, no match");
      }
    }
  };

  /** Processes the completion of successful image search */
  completeImageSearchRequest(response) {
    const record = response["data"]["records"][0];
    const { id } = record;
    const specialExhibition: boolean = response["data"]["specialExhibition"];

    // Navigate IMMEDIATELY. The artwork page renders a skeleton while
    // the CDN image loads. We previously awaited two Imgix transforms
    // before navigating (~1100ms of dead time); the skeleton + CDN
    // direct-fetch eliminates that wait entirely.
    this.phaseNavigation = Date.now();
    this.props.history.push({
      pathname: `${specialExhibition ? EXHIBITION : ARTWORK}/${id}`,
      state: {
        result: response,
        debugLog: this.state.debugLog,
        scanStartTime: this.scanStartTime,
        phases: {
          firstSubmit: this.phaseFirstSubmit,
          firstMatch: this.phaseFirstMatch,
          navigation: this.phaseNavigation,
        },
      },
    });
  }

  componentDidMount() {
    // FOC-136 watchdog: some visitors end up back on /scan after leaving the
    // Barnes (an app restore / stale tab reopening); a stale return must NOT
    // reopen the camera or submit — it films their personal life + feeds the
    // engine junk. If the last scan was more than SNAP_STALE_SESSION_INTERVAL
    // (30 min) ago, redirect Home BEFORE the camera initializes.
    //
    // Guarded to POP entries only: a deliberate Home→Scan (or scan-again from a
    // result) is a router PUSH and always passes, so a returning visitor who
    // taps Scan is never bounced. Only an involuntary POP entry — initial load,
    // app restore, back-nav — can trip it. A fresh session (no prior scan) passes.
    const lastScan = parseInt(
      localStorage.getItem(constants.SNAP_LAST_TIMESTAMP) || "",
      10
    );
    if (
      this.props.history.action === "POP" &&
      !Number.isNaN(lastScan) &&
      Date.now() - lastScan > constants.SNAP_STALE_SESSION_INTERVAL
    ) {
      this.props.history.replace({ pathname: HOME });
      return;
    }

    // Prewarm the API origin + Lambda before the user's first POST.
    // If the Focus app Lambda is cold-started, the first /api/scan/search
    // can wait 200ms-2s for the function to spin up — and that delay is
    // strongly correlated with the first-scan freeze Steve observes. Firing
    // a no-op fetch on mount means the function is warm by the time the
    // camera is ready and the user starts framing.
    //
    // We use GET (which the scan route doesn't handle → 404 quickly) so we
    // don't accidentally consume a real engine search slot. Fire-and-forget.
    fetch(constants.SCAN_SEARCH_URL, { method: "GET" }).catch(() => {});

    this.beginScanning();
  }

  render() {
    const { processImageCapture, snapAttempts, beginScanning } = this;
    const { sessionYieldedMatch, shouldBeScanning } = this.state;

    return (
      <Container className="camera-container" initialPose="exit" pose="enter">
        <Camera
          // @ts-ignore
          processImageCapture={processImageCapture}
          onCaptureFailure={this.recordCaptureFailure}
          sessionYieldedMatch={sessionYieldedMatch}
          beginScanning={beginScanning}
          snapAttempts={snapAttempts}
          shouldBeScanning={shouldBeScanning}
        />
        <DebugOverlay entries={this.state.debugLog} tsttm={this.scanStartTime ? Date.now() - this.scanStartTime : undefined} tsttf={this.state.tsttf} />
        {this.state.matchToConfirm && (
          <MatchConfirmation
            imageUrl={this.state.matchToConfirm.imageUrl}
            imageWidth={this.state.matchToConfirm.imageWidth}
            imageHeight={this.state.matchToConfirm.imageHeight}
            corners={this.state.matchToConfirm.corners}
          />
        )}
      </Container>
    );
  }
}

export default compose(withRouter, withTranslation)(CameraContainerComponent);
